<template>
  <div class="flex flex-col gap-2">
    <div class="relative">
      <TextInput
        v-model:value="search"
        label="hidden"
        class="search w-full rounded"
        :placeholder="$t('store_finder.box.placeholder')"
        data-testid="store-finder-search-input"
        @keyup.enter="onSearch"
        @input="dirty = true"
      />

      <FimButton
        id="search_stores"
        class="submit absolute right-px top-px size-12"
        type="link"
        :aria-label="$t('store_finder.a11y.search')"
        html-type="submit"
        no-padding
        :disabled="fetching"
        @click="onSearch"
      >
        <template #icon>
          <LazyIconFielmannSearch class="size-8" />
        </template>
      </FimButton>
      <FimButton
        id="locate_client"
        class="localize absolute inset-y-[2px] left-[2px] w-12 rounded-none bg-neutral-10"
        :disabled="fetching"
        type="link"
        :aria-label="$t('store_finder.a11y.locate')"
        html-type="button"
        no-padding
        @click="onLocate"
      >
        <template #icon>
          <LazyIconFielmannCompass class="size-6" />
        </template>
      </FimButton>
    </div>

    <div>
      <FimButton
        id="toggle_search_options"
        type="link"
        no-padding
        class="btn-options mt-3 border-0"
        :class="{ open: showOptions }"
        @click="toggleOptions"
      >
        {{
          showOptions
            ? $t('store_finder.box.less_search_options')
            : $t('store_finder.box.search_options')
        }}
      </FimButton>
    </div>
    <div v-if="showOptions" class="mb-6">
      <label for="search_options">
        {{ $t('store_finder.search.product') }}
      </label>
      <select
        id="search_options"
        v-model="searchOption"
        class="h-[48px] w-full flex-none rounded default:border-secondary-700 hover:border-black focus:border-secondary-700 focus:outline-none"
        @change="onChangeOption"
      >
        <option value="" disabled selected>
          {{ $t('store_finder.search.choose_option') }}
        </option>
        <option
          v-for="(title, index) in Object.keys(
            $tm('store_finder.search.options'),
          ).map((i) => ({
            value: i,
            name: $t(`store_finder.search.options.${i}`),
          }))"
          :key="index"
          :value="title.value"
        >
          {{ title.name }}
        </option>
      </select>
    </div>
  </div>
</template>

<script setup lang="ts">
import type GeolocationPosition from './IGeolocationPosition'
import { useGeocode } from '~/composables/useGeocode'
import { useStores } from '~/composables/useStores'
import { useMapMarker } from '~/composables/useMapMarker'
import { Action } from '~/plugins/toast'
import type { StoresParams } from '~/rpcMethods/stores'
import { useToast } from '~/composables/useToast'

const $i18n = useI18n()
const { alert } = useToast()
const route = useRoute()
const scope = effectScope()
onScopeDispose(() => scope.stop())
const location = ref<GeolocationPosition>()
const city = ref<string>()
const router = useRouter()
const { resetMarkers } = useMapMarker()

const {
  data: geocodeData,
  fetch: fetchGeocode,
  fetching: fetchingGeocode,
} = useGeocode()
const {
  fetch: fetchStores,
  search,
  searched,
  dirty,
  searchOption,
  fetching,
} = useStores()
const { logger } = useLogging()

const showOptions = ref<boolean>(searchOption.value !== 'ALL')
const locationUrlParam = computed(() => route.query.location)

if (!locationUrlParam.value) {
  search.value = ''
}

const getLocation = (): Promise<GeolocationPosition> =>
  new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('Geolocation is not available.'))
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve(pos)
      },
      (err) => {
        reject(err)
      },
    )
  })

const onLocate = async () => {
  location.value = await getLocation()
  await fetchGeocode({
    lat: location.value.coords.latitude,
    lng: location.value.coords.longitude,
  })
  resetMarkers()
  search.value = geocodeData.value?.results?.[0]?.formatted_address ?? ''
}

// Temp debug logging for this issue https://aboutyou.atlassian.net/browse/SCFIM-1767
// We should always get the same API response for the same search term
// We should always get the store with multiple branches in the city as the first result
const onSearch = async () => {
  await fetchGeocode({ search: search.value })
  resetMarkers()

  router.push({
    path: router.currentRoute?.value?.path,
    query: {
      location: search.value ?? '',
      ...(searchOption.value && { services: searchOption.value }),
    },
  })
  window.scrollTo(0, 0)
}

const computedLocation = computed(() => [
  geocodeData.value?.results[0]?.geometry?.location?.lat,
  geocodeData.value?.results[0]?.geometry?.location?.lng,
])

scope.run(() => {
  if (import.meta.server) {
    return
  }
  watch([computedLocation, fetchingGeocode], async () => {
    if (computedLocation.value && !fetchingGeocode.value) {
      await fetchStoresMethod()
    }
  })
  watch(geocodeData, (value) => {
    const locality = value?.results?.[0]?.address_components?.find(
      (addressComponent) => addressComponent.types.includes('locality'),
    )
    city.value = locality?.long_name
  })

  watch(locationUrlParam, (newValue) => {
    if (search.value !== newValue) {
      search.value = newValue as string
      onSearch()
    }
  })
})

const toggleOptions = () => {
  showOptions.value = !showOptions.value
}

const onChangeOption = () => {
  // update URL before calling onSearch because fetchStoresMethod depends on it
  if (searchOption.value) {
    router.push({
      query: {
        services: searchOption.value,
        ...(search.value && { location: search.value }),
      },
    })
  }
  if (search.value) {
    onSearch()
  }
}

const fetchStoresMethod = async () => {
  try {
    const searchParams: StoresParams = {
      lat: geocodeData.value?.results[0]?.geometry?.location
        ?.lat as unknown as number,
      lng: geocodeData.value?.results[0]?.geometry?.location
        ?.lng as unknown as number,
      geocodeResults: geocodeData.value?.results,
    }
    if (searchOption.value && searchOption.value !== 'ALL') {
      searchParams.type = searchOption.value
    }
    await fetchStores(searchParams)

    searched.value = true
    dirty.value = false
  } catch (error: unknown) {
    logger.error(error, {
      why: 'fetchStoresMethod',
      where: 'StoreFinderSearch.vue',
    })

    alert($i18n.t('error.connection'), Action.error)
  }
}
</script>

<style scoped>
.search :deep(input) {
  background: white;
  margin-top: 0;
  padding-right: 4rem;
  padding-left: 4rem;
  border-radius: 0.25rem;
}

.search :deep(label) {
  display: none;
}

.submit {
  border-color: transparent;
}

.submit :deep(svg) {
  max-width: none;
  max-height: none;
}

.localize {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: 0;
  border-top: 0;
  border-left: 0;
  height: 44px;
}

.btn-options :deep(svg) {
  transition: transform 200ms ease-out;
}

.btn-options.open :deep(svg) {
  transform: rotateZ(90deg);
}
</style>
